import { Card, List, Tag, Avatar, Skeleton } from 'antd'
import {
  PlusCircleOutlined,
  EditOutlined,
  ImportOutlined,
  FileTextOutlined,
  UserOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

interface Activity {
  id: string
  type: 'asset_added' | 'asset_updated' | 'data_imported' | 'report_generated'
  title: string
  description: string
  timestamp: string
  user: string
}

interface RecentActivitiesProps {
  activities: Activity[]
  loading?: boolean
  /** 点击「查看全部」时触发 */
  onViewAll?: () => void
}

const RecentActivities = ({ activities, loading = false, onViewAll }: RecentActivitiesProps) => {
  const typeConfig: Record<string, { icon: React.ReactNode; color: string; text: string }> = {
    asset_added: {
      icon: <PlusCircleOutlined />,
      color: 'green',
      text: '新增',
    },
    asset_updated: {
      icon: <EditOutlined />,
      color: 'blue',
      text: '更新',
    },
    data_imported: {
      icon: <ImportOutlined />,
      color: 'purple',
      text: '导入',
    },
    report_generated: {
      icon: <FileTextOutlined />,
      color: 'orange',
      text: '报告',
    },
  }

  const getTimeDisplay = (timestamp: string) => {
    const date = dayjs(timestamp)
    const now = dayjs()
    
    if (now.diff(date, 'hour') < 1) {
      return '刚刚'
    } else if (now.diff(date, 'day') < 1) {
      return date.fromNow()
    } else {
      return date.format('MM-DD HH:mm')
    }
  }

  if (loading) {
    return (
      <Card title="最近活动">
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    )
  }

  return (
    <Card
      title="最近活动"
      extra={
        onViewAll ? (
          <button
            type="button"
            onClick={onViewAll}
            className="text-sm text-blue-600 hover:text-blue-800 border-0 bg-transparent cursor-pointer p-0"
          >
            查看全部活动
          </button>
        ) : null
      }
    >
      <List
        dataSource={activities}
        renderItem={(activity) => {
          const config = typeConfig[activity.type]
          
          return (
            <List.Item>
              <List.Item.Meta
                avatar={
                  <div className="relative">
                    <Avatar
                      size="large"
                      icon={<UserOutlined />}
                      className="bg-gray-100 text-gray-600"
                    />
                    <div
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-white"
                      style={{ backgroundColor: config.color }}
                    >
                      {config.icon}
                    </div>
                  </div>
                }
                title={
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{activity.title}</div>
                    <Tag color={config.color} className="text-xs">
                      {config.text}
                    </Tag>
                  </div>
                }
                description={
                  <div>
                    <div className="text-gray-600">{activity.description}</div>
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center text-sm text-gray-500">
                        <UserOutlined className="mr-1" />
                        {activity.user}
                      </div>
                      <div className="text-sm text-gray-400">
                        {getTimeDisplay(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                }
              />
            </List.Item>
          )
        }}
      />
      
      {activities.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          暂无活动记录
        </div>
      )}
      
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="text-sm text-gray-600">
          今日活动统计：
          <span className="ml-2 font-medium">
            {activities.filter(a => dayjs(a.timestamp).isSame(dayjs(), 'day')).length} 条
          </span>
        </div>
      </div>
    </Card>
  )
}

export default RecentActivities